import {
  FolderCountResults,
  FolderForTree,
  FolderViewData,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';
import { IFolderMessage, IFolderTreeNode } from './FolderTree';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { FolderForTreeWrapper } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal';
import { createTreeStore, ITreeStore, NodeNotFoundType } from 'components/Tree/CommonTree/useTreeStore';
import { useFolderStore } from 'domains/portal/FolderStore';
import { IFolderNodeLoader, createTreeNode, loadFolderTreeNodes, ITreeNodeCreator } from './NodeLoaders';
import { StoreApi, UseBoundStore } from 'zustand';
import { scrollToElement as scrollToElementUtil } from 'utils/DomUtils';

export const RecentFolderId = 'RecentFolderId';
export const SystemFolderID = 'SYSTEM FOLDER';
export const WorkflowCatalogId = 'SERVICE CATALOG FOLDER';

export function getValidOrderNumber(orderNumber: number): number {
  return orderNumber === 0 ? Number.MAX_VALUE : orderNumber;
}

export function sortFolderTreeNodes(nodeA: ITreeNode<IFolderTreeNode>, nodeB: ITreeNode<IFolderTreeNode>): number {
  const a = nodeA.data!.folderNode;
  const b = nodeB.data!.folderNode;
  const orderOfNodeA = getValidOrderNumber(a.FolderID === RecentFolderId ? -3 : a.OrderNum ?? 0);
  const orderOfNodeB = getValidOrderNumber(b.FolderID === RecentFolderId ? -3 : b.OrderNum ?? 0);
  if (orderOfNodeA < orderOfNodeB) {
    return -1;
  }
  if (orderOfNodeA > orderOfNodeB) {
    return 1;
  }
  if (orderOfNodeA && !orderOfNodeB) {
    return 1;
  }
  if (!orderOfNodeA && orderOfNodeB) {
    return -1;
  }

  if (a && b) {
    return a.FolderName.localeCompare(b.FolderName);
  } else {
    return 0;
  }
}

export function scrollToElement(parentContainer?: HTMLElement | null | undefined) {
  if (!IS_TEST && parentContainer) {
    // scroll after expanding delay
    setTimeout(() => {
      const node = parentContainer.querySelector(`.dp-common-tree-node--active`)?.closest('li');
      const contHeight = parentContainer?.clientHeight || 0;
      const elemHeight = node?.clientHeight || 0;

      if (elemHeight > contHeight) {
        node?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } else {
        const elemClientRect = node?.getBoundingClientRect();
        const scrollHolderClientRect = parentContainer?.getBoundingClientRect();

        if (
          (elemClientRect?.bottom || 0) > (scrollHolderClientRect?.bottom || 0) ||
          (elemClientRect?.top || 0) < (scrollHolderClientRect?.top || 0)
        ) {
          node?.scrollIntoView({ block: 'end', behavior: 'smooth' });
        }
      }
    }, 300);
  }
}

export function isFolderForTreeWrappers(
  possiblyWrapped: FolderForTreeWrapper[] | FolderForTree[]
): possiblyWrapped is FolderForTreeWrapper[] {
  return isFolderForTreeWrapper(possiblyWrapped[0]);
}

export function isFolderForTreeWrapper(
  possiblyWrapped: FolderForTreeWrapper | FolderForTree
): possiblyWrapped is FolderForTreeWrapper {
  return (possiblyWrapped as FolderForTreeWrapper).FolderForTree !== undefined;
}

/**
 * ActionsStore uses `hasNode` to evaluate whether we need to CRUD event a folder
 *
 * separating that from PortalStore is a code-splitting concern.
 *
 * @param folderId
 */
export function folderTreeHasNode(folderId: string, treeStore?: ITreeStore<IFolderTreeNode>) {
  return !!treeStore?.actions.findNode(folderId);
}

export async function updateFolderCounter(store: ITreeStore<IFolderTreeNode>, countResult: FolderCountResults) {
  const node = store.actions.findNode(countResult.FolderId);
  if (node?.data?.folderNode) {
    node.data.folderNode.FolderCountResult = countResult;
    await store.actions.updateNode(node);
  }
}

/**
 * Rename a folder. Once all stores are updated, `renameFolderNameInFolderViewData` one time.
 * @param folderId to rename
 * @param folderName new name
 * @param stores which might contain folder. An array for the sake of desktop portal sidebar
 */
export async function renameFolderNode(
  folderId: string,
  folderName: string,
  stores: (ITreeStore<IFolderTreeNode> | undefined)[]
) {
  stores.forEach(store =>
    store?.enqueue(async store => {
      const node = store.actions.findNode(folderId);
      if (node) {
        store.actions.updateNode({
          ...node,
          name: folderName,
        });
      }
    })
  );
  //Updating FolderName on FolderViewData
  useFolderStore.getState().folderStoreActions.renameFolderNameInFolderViewData(folderId, folderName);
}

/**
 * Add a folder node to a tree (extracted from useFolderTreeStore)
 * @param folderMessage message telling to add node
 * @param folderForTree pre-fetched for pushing in
 * @param store tree store to add the node to
 * @param currentFolderIdGetter getter, so the currentFolderId comparisons are correct,
 *    even if this is concurrent with some navigation
 * @returns promise
 *
 * TODO feel like this could be added to useTreeStore, but not sure how many implementations of that would need it
 */
export async function addFolderNode(
  folderMessage: IFolderMessage,
  folderForTree: FolderForTree | null,
  currentFolderIdGetter: () => string,
  store?: ITreeStore<IFolderTreeNode>
) {
  if (!store) {
    return;
  }
  await store.enqueue(async () => {
    const node = store.actions.findNode(folderMessage.folderId);
    if (node) {
      await store.actions.reloadNode(folderMessage.folderId);
      return;
    }

    if (!folderForTree || folderForTree.FolderID !== folderMessage.folderId) {
      return;
    }

    const parentNode = await pushNodeToStore(folderForTree, store, currentFolderIdGetter);

    if (!folderMessage.doNotExpand && parentNode) {
      await store.actions.expandNode(parentNode);
    }
  });
}

/**
 * Push node to store or update it
 * @param folderForTree messge telling to add ndoe
 * @param store to update
 * @param currentFolderIdGetter getter, so the currentFolderId comparisons are correct,
 *    even if this is concurrent with some navigation
 * @returns promise
 */
export async function pushNodeToStore(
  folderForTree: FolderForTree,
  store: ITreeStore<IFolderTreeNode>,
  currentFolderIdGetter: () => string
) {
  if (folderForTree.EntityFolderID) {
    const parentNode = store.actions.findNode(folderForTree.EntityFolderID);
    const isExpanded = !!parentNode?.isExpanded;
    if (parentNode?.childNodes?.length) {
      await store.actions.pushNode(
        createTreeNode(folderForTree, currentFolderIdGetter(), false, parentNode),
        parentNode.nodeId
      );
    } else if (parentNode) {
      store.actions.updateNode({ ...parentNode, hasChildren: true });
      // load child nodes only if parent node is expanded
      if (isExpanded) {
        store.actions.loadNodes(parentNode.nodeId);
      }
    }

    return parentNode;
  }

  await store.actions.pushNode(createTreeNode(folderForTree, currentFolderIdGetter(), false));
}

/**
 *
 * @param treeId ID of tree, for scenarios we have multiple on the page
 * @param loader node loader
 * @param nodes if we have pre-loaded nodes from the initial HTML payload
 * @param sidebarContentSelector for scrolling the contents when a folder is selected
 * @param currentFolderIdGetter for scenarios the "current folder ID" is calculated, like desktop-portal sidebar tabs
 * @param registerFolderTreeStore for scenarios (like PortalStore) where multiple Tree Stores need to be registered
 */
export async function createFolderTreeStore(
  treeId: string,
  loader: IFolderNodeLoader,
  nodes: ITreeNode<IFolderTreeNode>[] = [],
  sidebarContentSelector: () => string,
  currentFolderIdGetter: () => string,
  registerFolderTreeStore?: (treeId: string, loader: IFolderNodeLoader) => void,
  treeNodeFactory: ITreeNodeCreator = createTreeNode
): Promise<UseBoundStore<StoreApi<ITreeStore<IFolderTreeNode>>>> {
  const loadRootNodes = async () => {
    if (!loader) {
      console.error('Loader not found!');
    }

    const rootFolders = await loader.getRootFolders();
    const rootNodes = rootFolders.map(folderNode => treeNodeFactory(folderNode, currentFolderIdGetter(), true));

    return rootNodes;
  };

  const loadNodes = async (parent?: ITreeNode<IFolderTreeNode>): Promise<ITreeNode<IFolderTreeNode>[]> => {
    if (parent) {
      return loadFolderTreeNodes(loader, parent, currentFolderIdGetter(), treeNodeFactory);
    } else {
      return loadRootNodes();
    }
  };

  const treeStore = createTreeStore<IFolderTreeNode>({
    treeId,
    nodes,
    loadNodes,
    onTreeLoaded: async () => {
      // mock node selection after tree load, for regular tree it will selected by server event
      if (IS_TEST) {
        const folderId = currentFolderIdGetter();
        await loadAndSelectNode(folderId);
        await expandNode(folderId);
      }
    },
    onNodeNotFound: async (node: string | ITreeNode<IFolderTreeNode>, type: NodeNotFoundType) => {
      if (type === NodeNotFoundType.Select) {
        await loadAndSelectNode(node as string);
      }
    },
    onNodeSelected: async node => {
      await onNodeSelected(node);
    },
    sortNodes: nodes => nodes.sort(sortFolderTreeNodes),
    onNodeDeleted: (node, parentNodeId) => {
      if (!parentNodeId) {
        return;
      }
      const parent = findNode(parentNodeId);
      // const nodeId = typeof node === 'string' ? node : node?.nodeId;
      if (parent && !parent?.childNodes?.length) {
        parent.hasChildren = false;
        parent.isExpanded = false;
        updateNode(parent);
      }

      // this used to also trigger navigation, BUT it triggered multiple navigation.
      // `parent?.nodeId && nodeId === Decisions.NavigationItemStorage.VisibleFolderInPortal` is exactly the check used
      // in Decisions.ClientEvents.FolderDeleteNotification to trigger navigation, so unless onNodeDeleted is called
      // from elsewhere, there's not a reason for this to also update navigation.
    },
    reloadNode: async (node: ITreeNode<IFolderTreeNode>, folderData?: IFolderTreeNode) => {
      const folderNode = folderData?.folderNode ?? (await loader.getSubFolder(node.nodeId));
      const parent = node.data!.parent;
      const newNode = {
        ...treeNodeFactory(folderNode, currentFolderIdGetter()),
        isExpanded: node.isExpanded,
        childNodes: node.childNodes,
      };
      newNode.data!.parent = parent;

      return newNode;
    },
  });

  registerFolderTreeStore?.(treeId, loader); // folder store, etc. get().actions.registerFolderStore(treeId, loader);

  const { selectNode, expandNode, navigateToTheDeep, updateNode, findNode, initialize } = treeStore.getState().actions;

  await initialize();

  /**
   * Expanding all parent nodes
   */
  const expandToRoot = async (node?: ITreeNode<IFolderTreeNode>) => {
    if (node) {
      await expandNode(node);
    }
    if (node?.data?.parent) {
      await expandToRoot(node.data.parent);
    }
  };

  const onNodeSelected = async (node: ITreeNode<IFolderTreeNode>) => {
    // expanding all parent nodes
    await expandToRoot(node);
    scrollToElement(document.querySelector(sidebarContentSelector()) as HTMLElement);
  };

  const loadAndSelectNode = createLoadAndSelectNodeHandler(navigateToTheDeep, selectNode);

  return treeStore;
}

/**
 * This `loadAndSelectNode` handler was split out into a factory, from a folder tree specific thing, to something we
 * could use for Workflows, too.
 * @param navigateToTheDeep action from tree store
 * @param selectNode action from tree store
 * @returns loadAndSelectNode handler, used especially in `onNodeNotFound` to expand to tree depths not yet fetched.
 */
export function createLoadAndSelectNodeHandler<T>(
  navigateToTheDeep: ITreeStore<T>['actions']['navigateToTheDeep'],
  selectNode: ITreeStore<T>['actions']['selectNode']
) {
  return async (folderId?: string) => {
    if (!folderId) {
      return;
    }
    let folderViewData: FolderViewData | undefined | null;
    if (!IS_TEST) {
      // during normal navigation, folderViewData should already be loaded, so this should not trigger additional fetch.
      folderViewData = await useFolderStore.getState().folderStoreActions.getFolderViewData(folderId);
    }
    const parentsIds = folderViewData?.Parents.map(p => p.FolderId) ?? [];

    if (!folderViewData?.ShowInTree && parentsIds.length) {
      folderId = parentsIds[0];
      parentsIds.shift();
    }

    // System folder is a virtual folder, we ignoring it in path
    const selectedNode = await navigateToTheDeep(folderId, parentsIds.reverse());

    if (selectedNode) {
      await selectNode(selectedNode);
    }
    return parentsIds;
  };
}

export function scrollToSelectedNode(container: HTMLElement) {
  setTimeout(() => {
    // grab element after delay, and scroll
    const treeRowWrapper = (container.getElementsByClassName('dp-common-tree-node--active')[0] as HTMLElement)
      ?.parentElement;
    treeRowWrapper && scrollToElementUtil(treeRowWrapper);
  }, 300);
}
